/* Built by Alpas Made. */
//
// VECTOR, the Kinetic Carving mascot. Portable, dependency free, no build step.
//
// This file draws the mascot: its faces, moods and motion.
//
// It is deliberately standalone so it can be dropped into any Kinetic Carving
// surface: the Help Center, the Shopify theme, a landing page, an email capture
// image. It imports nothing, touches no globals except the element it is given,
// and takes every colour from CSS custom properties so a host project styles it
// with its own tokens rather than editing this file.
//
//   <script src="/assets/vector.js"></script>
//   <vector-mark state="idle" size="40"></vector-mark>
//
//   el.state = 'in_flight'      // or setAttribute('state', ...)
//
// THE ONE RULE THAT MATTERS. Vector's face is not decoration on top of a status,
// it IS the status. Every face maps to exactly one named state of a real
// operation, and the face and the words a reader sees must come from the same
// value. A worried face has to mean a real low_confidence outcome, never a
// designer's choice, or the face becomes noise and stops being read.

(function () {
  'use strict';

  // ---------------------------------------------------------------- geometry
  //
  // One coordinate space, 0 to 100 both ways, for every part. The head is an
  // OUTLINE, stroked at 7 units, which is what gives the chunky read; the eyes
  // and nose are solid and sit on top, so a brow that touches an eye merges into
  // one angled shape. That merging is how eight faces come out of one drawing
  // instead of eight drawings.
  //
  // Three heads, not one, because the jaw carries half of every expression.

  /**
   * One piece of the drawing. Exactly one of these shapes:
   *   {p, s} a stroked path, s being the stroke width in coordinate units
   *   {p}    a filled path
   *   {r}    a rounded rectangle, [x, y, w, h, radius]
   * @typedef {{p?: string, s?: number, r?: number[]}} Part
   */

  /** @type {Record<string, string>} */
  var HEAD = {
    // teeth: anything going well, and the neutral default
    teeth: 'M50 6C72 6 88 21 88 41c0 9-3 15-8 19-4 3-8 4-10 7v7v10h-7V74h-4v10h-7V74h-4v10h-7V74h-4v10h-7V74v-7c-2-3-6-4-10-7-5-4-8-10-8-19C12 21 28 6 50 6Z',
    // wave: anything going wrong. A wavering jaw, read before any word is
    wave: 'M50 6C72 6 88 21 88 41c0 9-3 15-8 19-4 3-8 4-10 7v9c0 8-5 11-9 6-4-5-8 5-12 0-4-5-8 5-12 0-4-5-8 5-12-1v-14c-2-3-6-4-10-7-5-4-8-10-8-19C12 21 28 6 50 6Z',
    // grit: effort. Narrower teeth, more of them
    grit: 'M50 6C72 6 88 21 88 41c0 9-3 15-8 19-4 3-8 4-10 7v7v8h-5v-8h-2v8h-5v-8h-2v8h-5v-8h-2v8h-5v-8h-2v8h-5v-8h-2v8h-5v-8v-7c-2-3-6-4-10-7-5-4-8-10-8-19C12 21 28 6 50 6Z'
  };

  /** @type {Part[]} */
  var NOSE = [{ r: [45.5, 57, 4, 9, 2] }, { r: [50.5, 57, 4, 9, 2] }];

  // The lid ladder. Four openings that step evenly, so a blink or a squint is a
  // move ALONG this list rather than a separate drawing, and any two of them cut
  // together read as the same eye rather than two different ones.
  /** @type {Record<string, Part[]>} */
  var EYE = {
    wide: [{ r: [23, 29, 21, 25, 10] }, { r: [56, 29, 21, 25, 10] }],
    round: [{ r: [25, 31, 19, 22, 9] }, { r: [56, 31, 19, 22, 9] }],
    half: [{ r: [25, 38, 19, 12, 6] }, { r: [56, 38, 19, 12, 6] }],
    narrow: [{ r: [25, 40, 19, 9, 4.5] }, { r: [56, 40, 19, 9, 4.5] }],
    shut: [{ r: [25, 42.5, 19, 5, 2.5] }, { r: [56, 42.5, 19, 5, 2.5] }],
    happy: [{ p: 'M27 47c3-13 14-13 17 0', s: 7.5 }, { p: 'M56 47c3-13 14-13 17 0', s: 7.5 }],
    cross: [{ p: 'M28 35 43 50M43 35 28 50', s: 7.5 }, { p: 'M57 35 72 50M72 35 57 50', s: 7.5 }]
  };

  /** @type {Record<string, Part[]>} */
  var BROW = {
    uneven: [{ p: 'M24 25 44 21', s: 6 }, { p: 'M56 22 76 27', s: 6 }],
    set: [{ p: 'M24 27 44 31', s: 6 }, { p: 'M76 27 56 31', s: 6 }],
    sad: [{ p: 'M23 26 43 31', s: 6 }, { p: 'M77 26 57 31', s: 6 }]
  };

  /**
   * Move a pair of eyes without restating them.
   *
   * Glancing is the cheapest life there is: the head holds still and only the
   * eyes travel, which is what a person actually does when reading or thinking.
   * Moving the whole head instead is the thing that makes a mascot look like a
   * puppet.
   */
  /**
   * @param {Part[]} set
   * @param {number} dx
   * @param {number} [dy]
   * @returns {Part[]}
   */
  function shift(set, dx, dy) {
    return set.map(function (e) {
      if (!e.r) return e;
      var r = e.r.slice();
      r[0] += dx;
      r[1] += (dy || 0);
      return { r: r };
    });
  }

  /**
   * @param {string} head
   * @param {Part[]} eyes
   * @param {Part[]} [brows]
   * @returns {Part[]}
   */
  function parts(head, eyes, brows) {
    /** @type {Part[]} */
    var out = [{ p: HEAD[head], s: 7 }];
    return out.concat(eyes, brows || [], NOSE);
  }

  // ------------------------------------------------------------------- faces
  //
  // Motion is applied when PAINTING, not when rasterising, so a bob or a rock
  // costs nothing at all. Only a change of shape needs a second raster, which is
  // why just idle and in_flight carry frames.

  var FACES = {
    // Alive but calm. Two blinks and two glances per cycle, at uneven spacing so
    // it never reads as a metronome. Nothing here should ever pull the eye away
    // from what the reader came to do.
    idle: {
      label: 'resting',
      frames: [
        parts('teeth', EYE.round),
        parts('teeth', EYE.shut),
        parts('teeth', EYE.round),
        parts('teeth', shift(EYE.round, -3)),
        parts('teeth', EYE.round),
        parts('teeth', EYE.shut),
        parts('teeth', EYE.round),
        parts('teeth', shift(EYE.round, 3))
      ],
      hold: [2200, 140, 1200, 800, 1400, 140, 1000, 800],
      motion: function (t) { return { dy: -2.4 * Math.sin(t / 620) }; }
    },
    // Waiting, and slightly bored with it. Drowsy lids that open once a cycle,
    // as though checking whether it is his turn yet.
    queued: {
      label: 'waiting',
      frames: [
        parts('teeth', EYE.half),
        parts('teeth', EYE.shut),
        parts('teeth', EYE.half),
        parts('teeth', EYE.round),
        parts('teeth', EYE.half)
      ],
      hold: [1600, 380, 1500, 620, 1100],
      motion: function (t) { return { dy: -3.4 * Math.sin(t / 430) }; }
    },
    // Reading. The sweep is the work, and the blink in the middle of it is what
    // stops the sweep looking mechanical.
    in_flight: {
      label: 'searching',
      frames: [
        parts('teeth', shift(EYE.round, -3.5)),
        parts('teeth', EYE.round),
        parts('teeth', shift(EYE.round, 3.5)),
        parts('teeth', EYE.round),
        parts('teeth', shift(EYE.round, -3.5)),
        parts('teeth', EYE.shut),
        parts('teeth', shift(EYE.round, 3.5)),
        parts('teeth', EYE.round)
      ],
      hold: [420, 240, 420, 240, 380, 130, 400, 240]
    },
    // Delight. The eyes squeeze shut into arcs and pop open on the hop, which is
    // the 2400ms cycle the motion below runs on, so the two land together.
    succeeded: {
      label: 'found it',
      frames: [
        parts('teeth', EYE.happy),
        parts('teeth', EYE.wide),
        parts('teeth', EYE.happy)
      ],
      hold: [1500, 340, 560],
      motion: function (t) {
        var u = (t % 2400) / 2400;
        return { dy: u < 0.34 ? -7 * Math.sin(u / 0.34 * Math.PI) : 0 };
      }
    },
    // Not sure. The eyes dart, which is the tell: searching for something that
    // is not there, rather than looking at you.
    low_confidence: {
      label: 'not sure',
      frames: [
        parts('wave', EYE.round, BROW.uneven),
        parts('wave', shift(EYE.round, -3.5), BROW.uneven),
        parts('wave', EYE.round, BROW.uneven),
        parts('wave', shift(EYE.round, 3.5), BROW.uneven),
        parts('wave', EYE.round, BROW.uneven),
        parts('wave', EYE.shut, BROW.uneven)
      ],
      hold: [600, 520, 380, 520, 800, 160],
      motion: function (t) { return { rot: 4.2 * Math.sin(t / 560) }; }
    },
    // Broken. The only state whose eyes cannot look anywhere, because it is not
    // looking. What it does instead is flicker, like something failing to come
    // back up, and the flicker is deliberately irregular.
    failed: {
      label: 'something broke',
      frames: [
        parts('wave', EYE.cross),
        parts('wave', EYE.shut),
        parts('wave', EYE.cross),
        parts('wave', EYE.shut)
      ],
      hold: [2400, 110, 1300, 80],
      motion: function (t) { return { alpha: 0.72 + 0.28 * (0.5 + 0.5 * Math.sin(t / 480)) }; }
    },
    // Effort. The lids tighten and release rather than travel, because straining
    // is the opposite of glancing around.
    retrying: {
      label: 'trying again',
      frames: [
        parts('grit', EYE.half, BROW.set),
        parts('grit', EYE.narrow, BROW.set),
        parts('grit', EYE.half, BROW.set),
        parts('grit', EYE.round, BROW.set)
      ],
      hold: [460, 420, 420, 300],
      motion: function (t) { return { rot: 6 * Math.sin(t / 300) }; }
    },
    // Told it did not help. Wide eyes that keep dropping to the floor, and a
    // long slow blink. Looking down is the whole expression.
    unhelpful: {
      label: 'sorry that did not help',
      frames: [
        parts('wave', EYE.wide, BROW.sad),
        parts('wave', shift(EYE.wide, 0, 3), BROW.sad),
        parts('wave', EYE.wide, BROW.sad),
        parts('wave', EYE.shut, BROW.sad),
        parts('wave', shift(EYE.wide, 0, 3), BROW.sad)
      ],
      hold: [1200, 900, 700, 260, 1000],
      motion: function (t) {
        var v = 0.5 + 0.5 * Math.sin(t / 520);
        return { dy: 3 * v, rot: 2 * v };
      }
    }
  };

  // States a host system genuinely has that do not deserve their own drawing.
  // Kept as an explicit table rather than a silent fallback, so adding a state
  // is a decision somebody wrote down instead of a face quietly going neutral.
  //
  // rate_limited maps to queued because the reader is being asked to come back,
  // which is waiting rather than failing. It is emphatically NOT failed: nothing
  // broke, and a crossed-out face would send somebody to report a bug.
  var ALIAS = {
    rate_limited: 'queued',
    loading: 'in_flight',
    answered: 'succeeded',
    error: 'failed',
    empty: 'low_confidence'
  };

  function resolve(name) {
    if (FACES[name]) return name;
    if (ALIAS[name]) return ALIAS[name];
    return 'idle';
  }

  // ------------------------------------------------------------------ render

  var reduced = typeof matchMedia === 'function'
    && matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Rasterise one face into an alpha mask, then read back where the bone is,
   * and keep the dot positions.
   *
   * Why raster and sample rather than placing dots along a path: the head is an
   * outline, and an outline has no interior to walk. Stroking it into an
   * offscreen mask turns the line into an area, after which the sampler does not
   * care whether a part arrived as a stroke, a fill, or a rounded rectangle.
   * That is what lets one function draw every face.
   *
   * The drawing is inset to 90% and nudged down so a hop or a six degree rock
   * can never reach an edge. Getting that wrong is invisible until the single
   * frame where the top of the cranium is sliced flat.
   */
  function build(ctx, spec, W) {
    var s = W / 100, ox = 5 * s, oy = 7 * s, k = 0.9 * s;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, W, W);
    ctx.save();
    ctx.translate(ox, oy);
    ctx.scale(k, k);
    ctx.fillStyle = '#fff';
    ctx.strokeStyle = '#fff';
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    spec.forEach(function (o) {
      if (o.r) {
        ctx.beginPath();
        // roundRect is the only modern API used here. Where it is missing the
        // mark still draws, with square eyes, which is a far better failure than
        // no mascot at all.
        if (ctx.roundRect) ctx.roundRect(o.r[0], o.r[1], o.r[2], o.r[3], o.r[4]);
        else ctx.rect(o.r[0], o.r[1], o.r[2], o.r[3]);
        ctx.fill();
      } else if (o.s) {
        ctx.lineWidth = o.s;
        ctx.stroke(new Path2D(o.p));
      } else {
        ctx.fill(new Path2D(o.p));
      }
    });
    ctx.restore();

    // THE DEGRADATION RULE. The dot pitch is fixed in device pixels with a
    // floor. Above the floor the dots read as dots and the mark is engraved; at
    // the floor they touch and it becomes the solid outline on its own. One
    // rasteriser the whole way down, no second asset, nothing to switch over,
    // and no size at which the mark looks broken.
    var step = Math.max(3.2, 2.3 * k);
    var closed = step <= 3.4;
    var img = ctx.getImageData(0, 0, W, W).data;
    var pts = [];
    for (var y = step / 2; y < W; y += step) {
      for (var x = step / 2; x < W; x += step) {
        if (img[((y | 0) * W + (x | 0)) * 4 + 3] > 120) pts.push({ x: x, y: y });
      }
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, W, W);
    return { pts: pts, r: step * (closed ? 0.58 : 0.44), cx: 50 * s, cy: 7 * s + 82 * k,
             spec: spec, k: k, ox: ox, oy: oy };
  }

  /** Draw the parts as solid vector shapes, for marks below the floor. */
  function trace(ctx2, f, colour) {
    ctx2.save();
    ctx2.translate(f.ox, f.oy);
    ctx2.scale(f.k, f.k);
    ctx2.fillStyle = colour;
    ctx2.strokeStyle = colour;
    ctx2.lineJoin = 'round';
    ctx2.lineCap = 'round';
    f.spec.forEach(function (o) {
      if (o.r) {
        ctx2.beginPath();
        if (ctx2.roundRect) ctx2.roundRect(o.r[0], o.r[1], o.r[2], o.r[3], o.r[4]);
        else ctx2.rect(o.r[0], o.r[1], o.r[2], o.r[3]);
        ctx2.fill();
      } else if (o.s) {
        ctx2.lineWidth = o.s;
        ctx2.stroke(new Path2D(o.p));
      } else {
        ctx2.fill(new Path2D(o.p));
      }
    });
    ctx2.restore();
  }

  /** Read a colour token off the element, so a host project styles this with its own. */
  function ink(el, prop, fallback) {
    var v = getComputedStyle(el).getPropertyValue(prop).trim();
    return v || fallback;
  }

  // --------------------------------------------------------------- element

  var TPL = 'display:inline-block;line-height:0;flex:none';

  function VectorMarkImpl(el) {
    var canvas = document.createElement('canvas');
    canvas.style.display = 'block';
    canvas.setAttribute('aria-hidden', 'true');
    el.appendChild(canvas);
    var ctx = canvas.getContext('2d', { willReadFrequently: true });

    var cache = {};
    var W = 0;
    var cssPx = 40;
    var current = 'idle';
    var raf = 0;
    var t0 = 0;

    function frames(key) {
      var id = key + '@' + W;
      if (!cache[id]) {
        cache[id] = FACES[key].frames.map(function (spec) { return build(ctx, spec, W); });
      }
      return cache[id];
    }

    function size() {
      var css = parseFloat(el.getAttribute('size')) || 40;
      cssPx = css;
      // Render at up to 3x so the dots survive a high density screen, which is
      // where this is read most: a phone, in a workshop.
      var dpr = Math.min(window.devicePixelRatio || 1, 3);
      var next = Math.round(css * dpr);
      if (next === W) return;
      W = next;
      cache = {};
      canvas.width = W;
      canvas.height = W;
      canvas.style.width = css + 'px';
      canvas.style.height = css + 'px';
      el.style.width = css + 'px';
      el.style.height = css + 'px';
    }

    function paint(now) {
      if (!t0) t0 = now;
      var t = now - t0;
      // THE LEGIBILITY FLOOR, enforced here rather than trusted to callers.
      // Below 32 CSS pixels brows and eyes merge into mush, so the drawing
      // falls back to the idle face and the mark identifies rather than
      // reports. The accessible label keeps the REAL state, because a screen
      // reader has no legibility floor, and the words beside the mark carry
      // the state for everyone else.
      var shown = cssPx < 32 ? 'idle' : current;
      var face = FACES[shown];
      var set = frames(shown);

      var i = 0;
      if (set.length > 1) {
        var holds = face.hold || set.map(function () { return 600; });
        var total = holds.reduce(function (a, b) { return a + b; }, 0);
        var into = reduced ? 0 : t % total;
        while (into >= holds[i]) { into -= holds[i]; i++; }
      }
      var f = set[i];
      var m = (reduced || !face.motion) ? {} : face.motion(t);

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, W, W);
      ctx.save();
      ctx.globalAlpha = m.alpha === undefined ? 1 : m.alpha;
      if (m.rot) {
        ctx.translate(f.cx, f.cy);
        ctx.rotate(m.rot * Math.PI / 180);
        ctx.translate(-f.cx, -f.cy);
      }
      if (m.dy) ctx.translate(0, m.dy * (W / 100));
      var colour = ink(el, '--vector-ink', '#e83433');
      if (cssPx < 64) {
        // Below 64px the dots are the wrong medium: a fixed pitch across a mark
        // that small turns the silhouette to gravel on a 1x screen (measured
        // against the 128px mark, 32 to 63px scored worst, so the real face
        // read as a lumpy blob). Smaller marks TRACE the same geometry the
        // dots sample, so they are crisp at any density and identical in shape.
        // 64px and up keeps the dots, which are the look of the mascot.
        trace(ctx, f, colour);
      } else {
        ctx.fillStyle = colour;
        for (var n = 0; n < f.pts.length; n++) {
          ctx.beginPath();
          ctx.arc(f.pts[n].x, f.pts[n].y, f.r, 0, 6.283);
          ctx.fill();
        }
      }
      ctx.restore();
      raf = reduced ? 0 : requestAnimationFrame(paint);
    }

    function draw() {
      size();
      if (raf) cancelAnimationFrame(raf);
      // Paint the first frame SYNCHRONOUSLY, then start the loop.
      //
      // requestAnimationFrame does not fire in a hidden tab, so a mark created
      // while the page is backgrounded, prerendered, or being rasterised for a
      // screenshot would otherwise sit blank until somebody looked at it. It
      // self-heals on becoming visible, which is exactly what makes it easy to
      // miss and hard to debug. One synchronous paint costs nothing and
      // means the element is never empty after it is connected.
      paint(performance.now());
    }

    // THE WAITING LOOP.
    //
    // Only the moods that can still be true while the work is open. The other
    // five are outcomes, and an outcome on a timer is a face reporting failure
    // while the request is still running: the reader sees the sad skull and
    // stops waiting, at the moment they are least patient.
    //
    // queued reads as "still holding", which is true of a request that has not
    // come back, so the long beat is honest rather than filler.
    var BEATS = [
      { state: 'queued', ms: 1400 },
      { state: 'in_flight', ms: 4200 },
      { state: 'queued', ms: 1000 },
      { state: 'in_flight', ms: 5200 }
    ];
    var beat = 0;
    var beatTimer = 0;

    // Changes the drawing WITHOUT touching the accessible name. That split is
    // the whole reason the loop is safe: swapping the label every few seconds
    // would have a screen reader announce "waiting", "working", "waiting" at a
    // reader who asked one question, so the host names the state once and only
    // the picture moves.
    function show(name) {
      var next = resolve(name);
      if (next === current && raf) return;
      current = next;
      draw();
    }

    function stopLoop() {
      if (beatTimer) clearTimeout(beatTimer);
      beatTimer = 0;
    }

    return {
      set: function (name) {
        var next = resolve(name);
        if (next === current && raf) return;
        current = next;
        // The accessible name IS the state. A screen reader must get the same
        // information the face carries, or the face is decoration for some
        // readers and information for others.
        el.setAttribute('role', 'img');
        el.setAttribute('aria-label', 'Vector, ' + FACES[current].label);
        draw();
      },
      /**
       * Start or stop the waiting loop. Reduced motion keeps the true state and
       * no cycling, which is the same bargain every other animation here makes.
       * @param {boolean} on
       */
      loop: function (on) {
        stopLoop();
        if (!on || reduced) return;
        beat = 0;
        (function step() {
          var b = BEATS[beat % BEATS.length];
          show(b.state);
          beat += 1;
          beatTimer = setTimeout(step, b.ms);
        })();
      },
      redraw: draw,
      stop: function () {
        stopLoop();
        if (raf) cancelAnimationFrame(raf);
        raf = 0;
      }
    };
  }

  if (typeof customElements !== 'undefined' && !customElements.get('vector-mark')) {
    class VectorMark extends HTMLElement {
      constructor() {
        super();
        /** @type {ReturnType<typeof VectorMarkImpl> | null} */
        this._v = null;
      }

      static get observedAttributes() { return ['state', 'size', 'loop']; }

      connectedCallback() {
        if (!this._v) {
          this.style.cssText = TPL;
          this._v = VectorMarkImpl(this);
        }
        this._v.set(this.getAttribute('state') || 'idle');
        this._v.loop(this.hasAttribute('loop'));
      }

      disconnectedCallback() {
        // Stop the loop when the element leaves the page. A mark inside a panel
        // that gets replaced would otherwise keep a timer running forever, and
        // on a long admin session that is a real leak rather than a tidiness
        // point.
        if (this._v) this._v.stop();
      }

      /** @param {string} name */
      attributeChangedCallback(name) {
        if (!this._v) return;
        if (name === 'size') { this._v.redraw(); return; }
        if (name === 'loop') { this._v.loop(this.hasAttribute('loop')); return; }
        // A state set while looping wins and ends the loop: that is the work
        // finishing, and the outcome must not be cycled past.
        this._v.loop(false);
        this._v.set(this.getAttribute('state') || 'idle');
        if (this.hasAttribute('loop')) this._v.loop(true);
      }

      get state() { return this.getAttribute('state') || 'idle'; }
      set state(v) { this.setAttribute('state', v); }
    }

    customElements.define('vector-mark', VectorMark);
  }

  // Named export for anything that would rather not use a custom element, and
  // for asserting the contract in tests. Bracket access because this is a global
  // the host page does not declare.
  window['Vector'] = {
    STATES: Object.keys(FACES),
    ALIAS: ALIAS,
    /** @param {string} name */
    labelFor: function (name) { return FACES[resolve(name)].label; },
    resolve: resolve
  };
})();
